import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Quaternion, Vector3, Vector4 } from '@babylonjs/core/Maths/math.vector';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { Scene } from '@babylonjs/core/scene';

import { as2d } from '../core/canvas2d';

/**
 * The mesh half of "look at your controller, see what it does" — CLAUDE.md's task. Purely
 * mechanical: paint whatever title/lines it is given onto a canvas, position and billboard the
 * one plate that shows them, fade it in or out. `ControlHintsController` owns the look-at
 * detection and the binding table lookup; this class knows neither `Grabber` nor
 * `ControlBindings` at all, the same separation `LabelRenderer` (mesh) keeps from its own
 * caller (when to show a node/edge label).
 *
 * **One mesh, built once, re-textured** — CLAUDE.md's own brief, and the same construction
 * `LabelRenderer` already uses: the plane, its material and its `DynamicTexture` are built
 * in the constructor; `render()` only repaints the existing texture's canvas, and `setPose()`
 * only moves and re-rotates the existing mesh. Repainting is skipped entirely when the content
 * has not changed (`lastContentKey`), so holding a look at the same hand in the same mode does
 * not redraw the canvas every frame.
 *
 * **Billboarded and DOUBLESIDE with `backFaceCulling` left at the material default (`true`)** —
 * `CreatePlane`'s front is -Z (CLAUDE.md), so the yaw uses `atan2(-x, -z)`, and disabling
 * culling would reintroduce the mirrored-text bug the same document spends several paragraphs
 * on (coincident DOUBLESIDE triangles losing their depth tie in the wrong order). The plane,
 * material and `DynamicTexture` wiring below is the identical construction
 * `LabelledPlate.buildLabelledPlate` (`input/LabelledPlate.ts`) already uses (same
 * `sideOrientation`/`frontUVs`/`backUVs`, same `StandardMaterial` setup reading the texture as
 * both diffuse and emissive). What this class does *not* reuse is `buildLabelledPlate`'s own
 * text painting: `paintRoundedPlate` (private to `LabelledPlate.ts`, so not even callable from
 * here) draws exactly one centred line onto a plate sized for it, where a control-hints panel
 * is a title plus a variable-length list of "gesture — description" rows. Calling
 * `buildLabelledPlate` per row, or per repaint, would also mean building a new mesh every time
 * the looked-at hand or mode changes — the opposite of the "one mesh, re-textured" rule this
 * class exists to keep. `render()` below is this class's own multi-line painter, built the
 * same way `LabelRenderer`'s own single-line one already is: get the texture's 2D context,
 * draw, call `texture.update()`.
 */

/** Panel size in metres — big enough to hold a title and a handful of short lines, legible at
 *  the arm's-length distance a controller actually sits from the eye. Reported from a headset
 *  as "feels massive already" (CLAUDE.md task brief) — shrunk from the original 0.34x0.26 now
 *  that the descriptions themselves are far shorter (see `ControlBindings.ts`, `MAX_LINE_CHARS`
 *  below); the texture's own pixel budget (`TEXTURE_WIDTH`/`TEXTURE_HEIGHT`) is untouched, so
 *  this is purely a physical-size reduction in the room, not a text-fitting change. */
const PANEL_WIDTH = 0.28;
const PANEL_HEIGHT = 0.21;

const TEXTURE_WIDTH = 480;
const TEXTURE_HEIGHT = Math.round((PANEL_HEIGHT / PANEL_WIDTH) * TEXTURE_WIDTH);

const TITLE_FONT_PX = 26;
const LINE_FONT_PX = 20;
const GESTURE_COLOR = '#8FD1FF';
const TEXT_COLOR = '#E7ECF3';
const TITLE_COLOR = '#FFFFFF';
const BACKGROUND = '#12151aF0';
const BORDER = '#2A3038';

/** How far above the controller the panel's own bottom edge floats, in metres — clear of the
 *  hand itself. `setPose` anchors from this edge, not the plate's centre (see that method's own
 *  doc), so this value stays correct however tall the plate ends up being. */
const HEIGHT_OFFSET = 0.14;

/** How far to the side, in metres, so the panel does not sit directly on top of the hand it
 *  describes. Signed per hand in `setPose` — positive pushes toward the panel's own hand side,
 *  applied along the viewer's own rightward axis at the moment of posing (see `setPose`'s
 *  `sideAxis` parameter), not a fixed world axis — a player who is not facing world +Z would
 *  otherwise see the offset applied in a direction that has nothing to do with their own
 *  left/right. */
const SIDE_OFFSET = 0.1;

const PADDING_X = 18;
const TOP_PADDING_Y = 34;
const LINE_HEIGHT = 30;

/** `Mesh.DOUBLESIDE`, inlined so this module needs no value import of Mesh — the same trick
 *  `LabelledPlate.ts` and `LabelRenderer.ts` both already use. */
const DOUBLE_SIDED = 2;

/** How rounded the plate's corners are, in pixels of the underlying texture — matching the
 *  rest of the interface (`PaletteUI.buildLabelledPlate`'s own rounded tray plates), reported
 *  from a headset as the one thing this panel was missing. A fixed pixel value rather than a
 *  fraction of height (`PaletteUI`'s own `PLATE_CORNER_RADIUS_FRACTION`): that fraction is
 *  tuned for a near-square tray plate, and applied to this panel's much taller aspect ratio it
 *  would read as an oversized pill rather than a lightly rounded rectangle. */
const CORNER_RADIUS_PX = 16;

/**
 * Conservative average glyph width, in px, for the sans-serif line font at `LINE_FONT_PX` —
 * used only to size `MAX_LINE_CHARS` below, a structural ceiling on how long a binding's own
 * text may be, not for painting itself (painting always measures the real string with the real
 * font via `measureText`). Deliberately generous versus a typical sans-serif's true average
 * (nearer 10-11px at this size) so the ceiling stays a safe underestimate of how much actually
 * fits, leaving room for the bolder, wider `600`-weight gesture label.
 */
const AVG_GLYPH_WIDTH_PX = 12;

/**
 * The longest a single rendered line ("Gesture — description") can be before it risks running
 * past the panel's own drawable width — CLAUDE.md task brief: "make truncation structurally
 * impossible rather than unlikely." Derived from the texture's usable width (`TEXTURE_WIDTH`
 * minus both side paddings) divided by the conservative glyph-width estimate above, not a
 * number tuned by hand: `ControlBindings.test.ts` measures every real binding's
 * `gesture + ' — ' + description` against this so a description too long to fit fails a build
 * rather than silently clipping on a headset. It moves automatically if the panel's own width
 * or font size ever changes, since nothing here is copied by hand into `ControlBindings.ts`.
 */
export const MAX_LINE_CHARS = Math.floor((TEXTURE_WIDTH - PADDING_X * 2) / AVG_GLYPH_WIDTH_PX);

export interface ControlHintsLine {
  readonly gesture: string;
  readonly description: string;
}

export class ControlHintsPanel {
  private readonly mesh: Mesh;
  private readonly texture: DynamicTexture;
  private readonly material: StandardMaterial;

  /** Set once per distinct `render()` call so a repeated call with identical content is a
   *  no-op — see this file's own "One mesh, built once, re-textured" note. */
  private lastContentKey: string | null = null;

  /**
   * The `title`/`lines` most recently handed to `render()` — not a rendered pixel, which
   * `NullEngine` cannot produce and this project's tests do not pretend to check (CLAUDE.md:
   * "appearance is untestable"). What *is* checkable, and what this exists for, is the content
   * model that drove the paint: that a mode switch actually changed what would be shown, that
   * the dominant and off hands actually differ, and so on — see `ControlHintsController.test.ts`.
   */
  private lastRendered: { title: string; lines: readonly ControlHintsLine[] } | null = null;

  constructor(scene: Scene) {
    this.mesh = CreatePlane(
      'control-hints-panel',
      {
        width: PANEL_WIDTH,
        height: PANEL_HEIGHT,
        sideOrientation: DOUBLE_SIDED,
        frontUVs: new Vector4(0, 0, 1, 1),
        backUVs: new Vector4(1, 0, 0, 1),
      },
      scene,
    );
    this.mesh.isPickable = false;
    this.mesh.setEnabled(false);

    this.texture = new DynamicTexture(
      'control-hints-panel-texture',
      { width: TEXTURE_WIDTH, height: TEXTURE_HEIGHT },
      scene,
      true,
    );
    this.texture.hasAlpha = true;

    this.material = new StandardMaterial('control-hints-panel-material', scene);
    this.material.diffuseTexture = this.texture;
    this.material.useAlphaFromDiffuseTexture = true;
    this.material.emissiveTexture = this.texture;
    this.material.disableLighting = true;
    this.material.specularColor = Color3.Black();
    this.mesh.material = this.material;
  }

  /** True once `render()` has been called at least once — a caller can show the mesh before
   *  ever repainting it, which would flash whatever the constructor's blank canvas contains. */
  get hasContent(): boolean {
    return this.lastContentKey !== null;
  }

  /** What `render()` most recently painted — see `lastRendered`'s own doc. */
  get lastContent(): { title: string; lines: readonly ControlHintsLine[] } | null {
    return this.lastRendered;
  }

  /**
   * Repaints the panel with `title` and `lines`, skipping the redraw if nothing changed since
   * the last call. Does not itself show or hide the mesh — see `setAlpha`.
   */
  render(title: string, lines: readonly ControlHintsLine[]): void {
    const key = title + ' ' + lines.map((line) => `${line.gesture}${line.description}`).join(' ');
    this.lastRendered = { title, lines };

    if (key === this.lastContentKey) {
      return;
    }

    this.lastContentKey = key;

    const context = as2d(this.texture.getContext());
    context.clearRect(0, 0, TEXTURE_WIDTH, TEXTURE_HEIGHT);

    // Rounded, not a plain rect — CLAUDE.md task brief: "round the panel's corners... to match
    // the rest of the interface." The corners themselves are left untouched by the clearRect
    // above (transparent), which is what `hasAlpha`/`useAlphaFromDiffuseTexture` on this class's
    // own texture/material carry through to a genuinely rounded mesh edge — the same reasoning
    // `PaletteUI.paintRoundedPlate`'s own doc gives for its tray plates.
    tracePanelPath(context, TEXTURE_WIDTH, TEXTURE_HEIGHT, CORNER_RADIUS_PX);
    context.fillStyle = BACKGROUND;
    context.fill();
    context.strokeStyle = BORDER;
    context.lineWidth = 2;
    context.stroke();

    context.textAlign = 'left';
    context.textBaseline = 'alphabetic';

    context.fillStyle = TITLE_COLOR;
    context.font = `700 ${TITLE_FONT_PX}px system-ui, -apple-system, "Segoe UI", sans-serif`;
    context.fillText(title, PADDING_X, TOP_PADDING_Y);

    context.font = `${LINE_FONT_PX}px system-ui, -apple-system, "Segoe UI", sans-serif`;

    lines.forEach((line, index) => {
      const y = TOP_PADDING_Y + LINE_HEIGHT * (index + 1);

      context.fillStyle = GESTURE_COLOR;
      context.font = `600 ${LINE_FONT_PX}px system-ui, -apple-system, "Segoe UI", sans-serif`;
      context.fillText(line.gesture, PADDING_X, y);

      const gestureWidth = context.measureText(line.gesture).width;

      context.fillStyle = TEXT_COLOR;
      context.font = `${LINE_FONT_PX}px system-ui, -apple-system, "Segoe UI", sans-serif`;
      context.fillText(` — ${line.description}`, PADDING_X + gestureWidth, y);
    });

    this.texture.update();
  }

  /**
   * Sets the plate's opacity directly (0 = invisible). The mesh is only actually disabled at
   * 0, and only re-enabled above 0 — `ControlHintsController` drives a fade through this rather
   * than this class owning a fade clock itself, so the fade curve (hold, then linear, whatever
   * it ends up being) lives in exactly one place.
   */
  setAlpha(alpha: number): void {
    const clamped = Math.max(0, Math.min(1, alpha));

    if (clamped <= 0) {
      this.mesh.setEnabled(false);
      return;
    }

    this.mesh.setEnabled(true);
    this.material.alpha = clamped;
  }

  /**
   * Positions the plate beside `anchor` (a controller's own position) and billboards it toward
   * `eye`. `sideSign` pushes the plate to one side of the hand rather than directly on top of
   * it — positive for a right-hand anchor, negative for left, so the panel reads as "beside"
   * regardless of which hand is being looked at. `sideAxis` is the direction "sideways" actually
   * means, in world space, at this moment — the viewer's own rightward direction (flattened to
   * horizontal), not a fixed world axis: applying `SIDE_OFFSET` along raw world +X only reads as
   * "to the right" for a player facing world +Z, and reads as centred-or-reversed for anyone
   * facing another way, which is exactly the headset report this fixes ("on the left it seems
   * to center a bit... and clips into the controller").
   *
   * Anchored from the plate's own **bottom-centre**, not its centre — CLAUDE.md task brief:
   * "position it based on the center of the bottom of the plate, that will survive a resize as
   * well." `anchor + side + (0, HEIGHT_OFFSET, 0)` is the point the *bottom edge* should sit at;
   * the mesh's own position (its centre, since `CreatePlane` is centred on its pivot) is that
   * point shifted up by half the plate's own height. Because the up-shift is the only place
   * `PANEL_HEIGHT` enters this calculation, the bottom edge itself stays at exactly the same
   * spot however tall the plate is — a future change to `PANEL_HEIGHT` (or a taller plate for a
   * mode with more lines) cannot silently reposition where the plate appears to "start" relative
   * to the controller, only how far it grows upward from there.
   */
  setPose(anchor: Vector3, eye: Vector3, sideSign: 1 | -1, sideAxis: Vector3): void {
    const bottomCenter = anchor.add(sideAxis.scale(sideSign * SIDE_OFFSET)).add(new Vector3(0, HEIGHT_OFFSET, 0));
    this.mesh.position.copyFrom(bottomCenter);
    this.mesh.position.y += PANEL_HEIGHT / 2;

    // Yaw-billboarded exactly like ModeRingRenderer's own carousel anchor — see this file's own
    // doc for why the negation is load-bearing rather than cosmetic.
    const toEye = eye.subtract(this.mesh.position);
    (this.mesh.rotationQuaternion ??= Quaternion.Identity()).copyFrom(
      Quaternion.FromEulerAngles(0, Math.atan2(-toEye.x, -toEye.z), 0),
    );
  }

  dispose(): void {
    this.texture.dispose();
    this.material.dispose();
    this.mesh.dispose();
  }
}

/** Traces a rounded-rectangle path via `arcTo`, the same primitive and shape
 *  `PaletteUI.tracePlatePath` uses for its own tray plates (not imported from there: that
 *  function is module-private, and this module's own path is a fixed pixel radius rather than
 *  a fraction of height — see `CORNER_RADIUS_PX`'s own doc for why the two panels need
 *  different formulas). `arcTo` over the newer `roundRect` for the same reason PaletteUI picked
 *  it: this has to run against a headless canvas mock in tests as well as a real browser. */
function tracePanelPath(context: CanvasRenderingContext2D, width: number, height: number, radius: number): void {
  context.beginPath();
  context.moveTo(radius, 0);
  context.lineTo(width - radius, 0);
  context.arcTo(width, 0, width, radius, radius);
  context.lineTo(width, height - radius);
  context.arcTo(width, height, width - radius, height, radius);
  context.lineTo(radius, height);
  context.arcTo(0, height, 0, height - radius, radius);
  context.lineTo(0, radius);
  context.arcTo(0, 0, radius, 0, radius);
  context.closePath();
}
